import { isObject } from 'lodash';
import { guards } from './typeguards.helpers';
import {
  ExcludeError,
  MaybeCallbackOrError,
  MaybeCallback,
} from '@shared/models/type-helpers.model';

interface WithID {
  id: number | string;
}

/**
 * Create a lookup record (id => item) from an array of items.
 * @param items - Array of items to create a lookup record from.
 * @returns Lookup record (id => item).
 */
export const toLookup = <T extends WithID>(items: T[]): Record<T['id'], T> =>
  items.reduce(
    (acc, item) => {
      acc[item.id as T['id']] = item; // Way faster than spread operator
      return acc;
    },
    {} as Record<T['id'], T>,
  );

export const toBoolean = (
  bool: string | number | undefined | null | boolean,
): boolean => {
  if (!bool || bool === 'false') {
    return false;
  }
  return !!bool;
};

const isValidSemver = (version: string): boolean =>
  /^(\d+|x)\.(\d+|x)\.(\d+|x)$/.test(version);

interface ISemverCompareTolerance {
  major?: number;
  minor?: number;
  patch?: number;
}

const decreaseVersion = (
  version: string,
  tolerance: ISemverCompareTolerance,
) => {
  if (!isValidSemver(version)) {
    throw new Error('Invalid semver');
  }

  const versionParts = version
    .split('.')
    .map((v) => (v === 'x' ? 'x' : Number(v)));
  const toleranceParts = [tolerance.major, tolerance.minor, tolerance.patch];

  for (let i = 0; i < versionParts.length; i++) {
    const part = versionParts[i];
    if (part !== 'x') {
      versionParts[i] = (part ?? 0) - Math.abs(toleranceParts[i] ?? 0);
    }
  }

  return versionParts.map((p) => (p === 'x' ? 'x' : Math.max(0, p))).join('.');
};

/**
 * Check if a version is greater or equal to another version, with a tolerance.
 * @param version - The version to check.
 * @param comparedTo - The version to compare to.
 * @param tolerance - The tolerance to use.
 * @returns True if the version is greater or equal to the comparedTo version, false otherwise.
 */
export const isGreaterOrEqualVersion = (
  version: string | undefined,
  comparedTo: string,
  tolerance: ISemverCompareTolerance = {},
): boolean => {
  comparedTo = decreaseVersion(comparedTo, tolerance);
  if (!version || !isValidSemver(version) || !isValidSemver(comparedTo)) {
    throw new Error('Invalid semver');
  }

  const versionParts = version
    .split('.')
    .map((v) => (v === 'x' ? Number.MAX_VALUE : Number(v)));
  const compareVersionParts = comparedTo
    .split('.')
    .map((v) => (v === 'x' ? 0 : Number(v)));

  for (let i = 0; i < versionParts.length; i++) {
    const versionPart = versionParts[i];
    const compareVersionPart = compareVersionParts[i];

    if (versionPart === undefined || compareVersionPart === undefined) {
      return false;
    }
    if (versionPart > compareVersionPart) {
      return true;
    }
    if (versionPart < compareVersionPart) {
      return false;
    }
  }
  return true;
};

type Id = string | number | undefined | null;
type IdObject = { id?: Id };

export const isEqualIds = (id1: Id | IdObject, id2: Id | IdObject): boolean => {
  const id1Value = isObject(id1) ? id1.id : id1;
  const id2Value = isObject(id2) ? id2.id : id2;

  if (typeof id1Value !== typeof id2Value) {
    return String(id1Value) === String(id2Value);
  }
  return id1Value === id2Value;
};

export const getListItemKey = (index?: number, value?: any) => {
  return typeof index === 'number' ? value + '-' + index : undefined;
};

/**
 * Wraps a maybe error of unknown type in an Error object.
 * If the error is already an Error object, it is returned as is.
 * @param maybeError - The maybe error to wrap.
 * @returns An Error object.
 */
export const wrapError = (maybeError: unknown): Error => {
  return maybeError instanceof Error
    ? maybeError
    : new Error(String(maybeError));
};

type TryCatchSuccess<Type> = { value: Type; ok: true; error: undefined };
type TryCatchFailure = { value: undefined; ok: false; error: Error };
type TryCatchFailureWithDefault<DefaultType> = {
  value: DefaultType;
  ok: false;
  error: Error;
};
type TryCatchResult<Type, DefaultType> =
  | TryCatchSuccess<Type>
  | TryCatchFailureWithDefault<DefaultType>
  | TryCatchFailure;

/**
 * Wraps a function in a try/catch block and returns the result or an Error object if an error occurs.
 * Use to help with deeply nested code where adding another try catch block seems excessive,
 * or when you simply want to assign a dangerous function return directly without using let + try {} catch (e) {}
 *
 * @param fn - The function to wrap.
 * @param defaultValue - The default value to return if an error occurs.
 * @returns The result of the function or an Error object if an error occurs.
 */
export function tryCatch<Type>(
  fn: () => Type,
): TryCatchSuccess<Type> | TryCatchFailure;

export function tryCatch<Type, DefaultType>(
  fn: () => Type,
  defaultValue: DefaultType,
): TryCatchSuccess<Type> | TryCatchFailureWithDefault<DefaultType>;

export function tryCatch<Type, DefaultType>(
  fn: () => Type,
  defaultValue?: DefaultType,
): TryCatchResult<Type, DefaultType> {
  try {
    return { value: fn(), ok: true, error: undefined };
  } catch (maybeError) {
    if (guards.is.defined(defaultValue)) {
      return { value: defaultValue, ok: false, error: wrapError(maybeError) };
    }
    return { value: undefined, ok: false, error: wrapError(maybeError) };
  }
}

/**
 * Wraps a function in a try/catch block and returns the result or an Error object if an error occurs.
 * Use to help with deeply nested code where adding another try catch block seems excessive,
 * or when you simply want to assign a dangerous function return directly without using let + try {} catch (e) {}
 *
 * @param fn - The function to wrap.
 * @param defaultValue - The default value to return if an error occurs.
 * @returns The result of the function or an Error object if an error occurs.
 */
export async function tryCatchAwaitable<Type>(
  promise: Awaitable<Type>,
): Promise<TryCatchSuccess<Type> | TryCatchFailure>;

export async function tryCatchAwaitable<Type, DefaultType>(
  promise: Awaitable<Type>,
  defaultValue: DefaultType,
): Promise<TryCatchSuccess<Type> | TryCatchFailureWithDefault<DefaultType>>;

export async function tryCatchAwaitable<Type, DefaultType>(
  promise: Awaitable<Type>,
  defaultValue?: DefaultType,
): Promise<TryCatchResult<Type, DefaultType>> {
  try {
    return { value: await promise, ok: true, error: undefined };
  } catch (maybeError) {
    if (guards.is.defined(defaultValue)) {
      return { value: defaultValue, ok: false, error: wrapError(maybeError) };
    }
    return { value: undefined, ok: false, error: wrapError(maybeError) };
  }
}

/**
 * @deprecated Use assertNotError instead. (Can't be removed due to other PRs currently using it)
 * Throws an error if the result is an error else return the result.
 * @param result - The result to check.
 * @returns The result if it is not an error.
 */
export const throwIfError = <T>(result: T | Error): ExcludeError<T> => {
  assertNotError(result);
  return result as ExcludeError<T>;
};

/**
 * @deprecated Use assert.not.error instead. (Can't be removed due to other PRs currently using it)
 * Throws an error if the value is an error and narrows the type to the non-error type.
 * @param value - The value to check.
 * @returns The value if it is not an error.
 */
export function assertNotError<T>(value: T): asserts value is ExcludeError<T> {
  if (guards.is.error(value)) throw value;
}

/**
 * Throw an error if any of the items is an error.
 * @param items - The items to check
 * @returns The items if they are not validation errors
 */
export const throwIfArrayContainsError = <T>(
  array: (T | Error)[],
): ExcludeError<T>[] => {
  array.forEach(assertNotError); // This will throw if any item is an error
  return array as ExcludeError<T>[];
};

/**
 * Resolve wrapper function or return a static item
 * @param fnOrValue - Either a function that returns an item or an item
 * @returns
 */
export const resolveCallbackFunction = <T>(fnOrValue: MaybeCallback<T>): T =>
  guards.is.function(fnOrValue) ? fnOrValue() : fnOrValue;

/**
 * Like tryCatch, but also let you pass a an error to trigger catch.
 * Can both apss values or errors right away or wrap them in a function.
 * @param fnOrValue - The function to wrap or a non-funtion item that could be an error
 * @param defaultValue - The default value to return if an error occurs.
 * @returns The result of the function or an Error object if an error was found
 */
export function catchError<Type>(
  fnOrValue: MaybeCallbackOrError<Type>,
): TryCatchSuccess<ExcludeError<Type>> | TryCatchFailure;

export function catchError<Type, DefaultType>(
  fnOrValue: MaybeCallbackOrError<Type>,
  defaultValue: DefaultType,
):
  TryCatchSuccess<ExcludeError<Type>> | TryCatchFailureWithDefault<DefaultType>;

export function catchError<Type, DefaultType>(
  fnOrValue: MaybeCallbackOrError<Type>,
  defaultValue?: DefaultType,
): TryCatchResult<ExcludeError<Type>, DefaultType> {
  // If the fnOrValue is a function, use tryCatch
  if (guards.is.function(fnOrValue)) {
    const result = tryCatch(fnOrValue, defaultValue);

    // Handle errors that were returned by the function by recursively calling catchError
    // This ensures we can unwrap multiple layers of wrapper functions
    return catchError<Type, DefaultType>(
      result.error ? result.error : result.value,
      defaultValue as DefaultType, // Should not need casting but I'm rage quitting on types now
    );
  }

  // If fnOrValue was provided as an error, return an error
  if (guards.is.error(fnOrValue)) {
    return { value: defaultValue as DefaultType, ok: false, error: fnOrValue };
  }
  return { value: fnOrValue, ok: true, error: undefined };
}
